import { createFileRoute } from '@tanstack/react-router'
import { PageHeader } from '@/components/shared/page-header'
import { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, Plus, Users, X, PauseCircle } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useCreators, useBulkUpdateCreatorStatus } from '@/hooks/use-creators'
import { UI } from '@/lib/constants'
import { CreatorDialog } from '@/components/creators/creator-form-dialog'
import { CreatorRow } from '@/components/creators/creator-row'
import { CreatorFilters, type CreatorFilterValues } from '@/components/creators/creator-filters'
import { AppPagination } from '@/components/shared/app-pagination'
import { EmptyState } from '@/components/shared/empty-state'
import { SortableHeader, type SortDirection } from '@/components/shared/sortable-header'
import { TooltipProvider } from '@/components/ui/tooltip'

export const Route = createFileRoute('/_app/creators/')({
  component: CreatorsPage,
})

function TableRowSkeleton() {
  return (
    <TableRow>
      {/* Checkbox */}
      <TableCell className="w-10 pr-0">
        <Skeleton className="h-4 w-4" />
      </TableCell>
      {/* Creator */}
      <TableCell>
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 rounded-full" />
          <div className="space-y-1">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      </TableCell>
      {/* Audience */}
      <TableCell className="hidden lg:table-cell">
        <div className="space-y-1">
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-3 w-8" />
        </div>
      </TableCell>
      {/* Tier & Contract */}
      <TableCell className="hidden md:table-cell">
        <div className="space-y-1">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-3 w-16" />
        </div>
      </TableCell>
      {/* Coupon */}
      <TableCell className="hidden lg:table-cell">
        <div className="space-y-1">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-3 w-16" />
        </div>
      </TableCell>
      {/* Receita */}
      <TableCell className="hidden xl:table-cell">
        <Skeleton className="h-4 w-20" />
      </TableCell>
      {/* Activity */}
      <TableCell className="hidden xl:table-cell">
        <div className="space-y-1">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 w-20" />
        </div>
      </TableCell>
      {/* Alerts */}
      <TableCell className="w-10 hidden sm:table-cell">
        <Skeleton className="h-4 w-4" />
      </TableCell>
      {/* Actions */}
      <TableCell className="w-10 text-right">
        <Skeleton className="h-8 w-8 inline-block" />
      </TableCell>
    </TableRow>
  )
}

const DEFAULT_FILTERS: CreatorFilterValues = {
  search: '',
  status: 'all',
  tierId: 'all',
  niche: 'all',
  type: 'all',
  programId: 'all',
}

function CreatorsPage() {
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<CreatorFilterValues>(DEFAULT_FILTERS)
  const [sortKey, setSortKey] = useState<string | null>('createdAt')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const {
    data: queryData,
    isLoading,
    error,
  } = useCreators(page, 20, filters, { sortBy: sortKey, sortDirection })

  const bulkUpdateStatus = useBulkUpdateCreatorStatus()

  const creators = queryData?.data
  const meta = queryData?.meta

  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const allSelected = useMemo(() => {
    if (!creators?.length) return false
    return creators.every((c) => selectedIds.has(c.id))
  }, [creators, selectedIds])

  const someSelected = useMemo(() => {
    if (!creators?.length) return false
    return creators.some((c) => selectedIds.has(c.id)) && !allSelected
  }, [creators, selectedIds, allSelected])

  const handleFiltersChange = (newFilters: CreatorFilterValues) => {
    setFilters(newFilters)
    setPage(1)
    setSelectedIds(new Set())
  }

  const handleSort = (key: string, direction: SortDirection) => {
    setSortKey(direction ? key : null)
    setSortDirection(direction)
    setPage(1)
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked && creators) {
      setSelectedIds(new Set(creators.map((c) => c.id)))
    } else {
      setSelectedIds(new Set())
    }
  }

  const handleSelectOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const handleBulkPause = () => {
    const ids = Array.from(selectedIds)
    bulkUpdateStatus.mutate(
      { creatorIds: ids, status: 'paused' },
      { onSuccess: () => setSelectedIds(new Set()) }
    )
  }

  const clearSelection = () => setSelectedIds(new Set())

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-2">
          <AlertCircle className="h-8 w-8 text-destructive mx-auto" />
          <p className="text-muted-foreground">Erro ao carregar creators</p>
          <p className="text-sm text-destructive">{error.message}</p>
        </div>
      </div>
    )
  }

  const hasActiveFilters = Object.entries(filters).some(
    ([key, value]) => value !== DEFAULT_FILTERS[key as keyof CreatorFilterValues]
  )

  return (
    <TooltipProvider delayDuration={300}>
      <div className="space-y-6">
        <PageHeader
          title="Creators"
          description={
            meta && !isLoading
              ? hasActiveFilters
                ? `Gerencie sua base de criadores de conteúdo e afiliados · ${meta.total} de ${meta.total}`
                : `Gerencie sua base de criadores de conteúdo e afiliados · ${meta.total} creators`
              : 'Gerencie sua base de criadores de conteúdo e afiliados'
          }
          actions={
            <ChonkyButton variant="primary" onClick={() => setIsDialogOpen(true)}>
              <Plus className="h-3.5 w-3.5" /> Novo Criador
            </ChonkyButton>
          }
        />

        <CreatorFilters filters={filters} onFiltersChange={handleFiltersChange} />

        <div className="max-w-[calc(100vw-2rem)] md:max-w-[calc(100vw-(--spacing(64))-3rem)]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10 pr-0">
                  <Checkbox
                    checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                    onCheckedChange={(checked) => handleSelectAll(checked === true)}
                    aria-label="Selecionar todos"
                  />
                </TableHead>
                <SortableHeader
                  sortKey="name"
                  currentSort={sortKey}
                  currentDirection={sortDirection}
                  onSort={handleSort}
                >
                  Creator
                </SortableHeader>
                <SortableHeader
                  sortKey="instagramFollowers"
                  currentSort={sortKey}
                  currentDirection={sortDirection}
                  onSort={handleSort}
                  className="hidden lg:table-cell"
                >
                  Audiência
                </SortableHeader>
                <TableHead className="hidden md:table-cell">Tier & Contrato</TableHead>
                <TableHead className="hidden lg:table-cell">Cupom</TableHead>
                <SortableHeader
                  sortKey="totalSalesAmount"
                  currentSort={sortKey}
                  currentDirection={sortDirection}
                  onSort={handleSort}
                  className="hidden xl:table-cell"
                >
                  Receita
                </SortableHeader>
                <SortableHeader
                  sortKey="lastPostAt"
                  currentSort={sortKey}
                  currentDirection={sortDirection}
                  onSort={handleSort}
                  className="hidden xl:table-cell"
                >
                  Atividade
                </SortableHeader>
                <TableHead className="w-10 hidden sm:table-cell" />
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: UI.SKELETON_TABLE_ROWS }).map((_, i) => (
                  <TableRowSkeleton key={i} />
                ))
              ) : creators?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="p-0">
                    {hasActiveFilters ? (
                      <EmptyState
                        icon={Users}
                        title="Nenhum resultado encontrado"
                        description="Tente ajustar os filtros para encontrar o que procura."
                        actionLabel="Limpar filtros"
                        onAction={() => handleFiltersChange(DEFAULT_FILTERS)}
                        className="sticky left-0 w-[calc(100vw-2rem)] md:w-[calc(100vw-theme(spacing.64)-3rem)]"
                      />
                    ) : (
                      <EmptyState
                        icon={Users}
                        title="Nenhum creator cadastrado"
                        description="Adicione seu primeiro criador de conteúdo para começar o programa."
                        actionLabel="Adicionar Creator"
                        onAction={() => setIsDialogOpen(true)}
                        className="sticky left-0 w-[calc(100vw-2rem)] md:w-[calc(100vw-theme(spacing.64)-3rem)]"
                      />
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                creators?.map((creator) => (
                  <CreatorRow
                    key={creator.id}
                    creator={creator}
                    selected={selectedIds.has(creator.id)}
                    onSelectChange={(checked) => handleSelectOne(creator.id, checked)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <AppPagination meta={meta} onPageChange={setPage} isLoading={isLoading} />

        {/* Bulk actions bar */}
        {selectedIds.size > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4 fade-in-0 duration-200">
            <div className="flex items-center gap-3 bg-background border rounded-lg shadow-lg px-4 py-3">
              <span className="text-sm font-medium whitespace-nowrap">
                {selectedIds.size} selecionado{selectedIds.size > 1 ? 's' : ''}
              </span>
              <div className="h-4 w-px bg-border" />
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkPause}
                disabled={bulkUpdateStatus.isPending}
              >
                <PauseCircle className="mr-2 h-4 w-4" />
                Pausar
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={clearSelection}>
                <X className="h-4 w-4" />
                <span className="sr-only">Limpar seleção</span>
              </Button>
            </div>
          </div>
        )}

        <CreatorDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} />
      </div>
    </TooltipProvider>
  )
}
